import React, { useContext, useEffect } from "react";
import {
  Container,
  Typography,
  Grid,
  Box,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { Language as LanguageIcon } from "@mui/icons-material";

import { BreadcrumbContext } from "../../../../components/Breadcrumb";
import { useOrganizationSites } from "../../../../hooks/useOrganizationSites";
import SiteCard from "../../../../components/SiteCard";
import { EmptyState } from "../../../../components/Common";

/**
 * Organization All Sites Page
 * 
 * This page displays all sites across all accounts/collections within an organization.
 * It provides a unified view of all sites, using the same SiteCard component that's
 * used in account-level site lists for consistency.
 * 
 * Features:
 * - Displays all sites from all accounts in the organization
 * - Uses SiteCard component for consistent site representation
 * - Shows account context for each site
 * - Handles loading and error states
 * - Responsive grid layout
 */
const AllSites = () => {
  const { setBreadcrumb } = useContext(BreadcrumbContext);
  const { organizationId } = useParams();
  
  // Fetch all sites for the specified organization
  const { sites, loading, error } = useOrganizationSites(organizationId);

  // Set up breadcrumb navigation
  useEffect(() => {
    setBreadcrumb([
      { to: "/", text: "Home", active: false },
      { to: null, text: "All Sites", active: true },
    ]);
  }, [setBreadcrumb]);

  // Handle loading state
  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={48} />
        </Box>
      </Container>
    );
  }

  // Handle error state
  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography variant="h4" gutterBottom>
          All Sites
        </Typography>
        <Alert severity="error" sx={{ mt: 2 }}>
          Error loading sites: {error}
        </Alert>
      </Container>
    );
  }

  // Handle empty state
  if (sites.length === 0) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography variant="h4" gutterBottom>
          All Sites
        </Typography>
        <EmptyState
          icon={<LanguageIcon sx={{ fontSize: 64 }} />}
          title="No Sites Found"
          description="There are no sites in this organization yet."
        />
      </Container>
    );
  }

  // Render sites grid
  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Page Title */}
      <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
        All Sites
      </Typography>

        {/* Summary information */}
        <Box sx={{ mt: 4, mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
                Showing {sites.length} sites across {new Set(sites.map(s => s.accountId)).size} collections
            </Typography>
        </Box>

      {/* Sites Grid */}
      <Grid container spacing={3}>
        {sites.map((site) => (
          <Grid item xs={12} key={`${site.accountId}-${site.id}`}>
            <SiteCard
              site={site}
              // Show collection context within the card
              showAccountContext={true}
              accountName={site.accountName}
              // Pass account context to ensure proper navigation
              accountId={site.accountId}
              // Disable template-specific features for organization view
              isTemplatesSite={false}
              isAdminTemplates={false}
              // Site management handlers - these will navigate to account-level actions
              onManage={() => {
                // Navigate to account-level site management
                window.location.href = `/account/${site.accountId}/site-details/${site.id}`;
              }}
            />
          </Grid>
        ))}
      </Grid>


    </Container>
  );
};

export default AllSites;